'use client';

import React from 'react';
import { Sparkles } from 'lucide-react';
import type { ReceiptAiPayload } from '@/lib/receiptAiAnalysis';
import { ExtractionSourceBadge } from '@/components/finance/ExtractionSourceBadge';

export interface OcrConfidenceIndicatorProps {
  receiptResult: ReceiptAiPayload;
}

function confidenceTier(confidence: number) {
  if (confidence >= 0.8) {
    return {
      border: 'rgb(34,197,94)',
      bg: 'rgba(34,197,94,0.08)',
      icon: 'rgb(22,163,74)',
      label: 'rgb(22,163,74)',
      bar: 'rgb(34,197,94)',
      text: 'High accuracy',
    };
  }
  if (confidence >= 0.5) {
    return {
      border: 'rgb(245,158,11)',
      bg: 'rgba(245,158,11,0.08)',
      icon: 'rgb(217,119,6)',
      label: 'rgb(217,119,6)',
      bar: 'rgb(245,158,11)',
      text: 'Moderate — verify fields',
    };
  }
  return {
    border: 'rgb(239,68,68)',
    bg: 'rgba(239,68,68,0.08)',
    icon: 'rgb(220,38,38)',
    label: 'rgb(220,38,38)',
    bar: 'rgb(239,68,68)',
    text: 'Low — please verify',
  };
}

/**
 * Compact OCR confidence meter shown after the receipt AI returns a result.
 * Color-coded by confidence tier (green ≥80%, amber ≥50%, red <50%).
 */
export function OcrConfidenceIndicator({ receiptResult }: OcrConfidenceIndicatorProps) {
  const tier = confidenceTier(receiptResult.confidence);
  const pct = Math.round(receiptResult.confidence * 100);

  return (
    <div
      className="flex items-center gap-2 rounded-lg border px-3 py-2 text-xs"
      style={{ borderColor: tier.border, backgroundColor: tier.bg }}
    >
      <Sparkles className="h-3.5 w-3.5 shrink-0" style={{ color: tier.icon }} />
      <span className="font-medium" style={{ color: tier.label }}>
        OCR Confidence: {pct}%
      </span>
      <ExtractionSourceBadge
        source={receiptResult.extraction_source ?? null}
        model={receiptResult.extraction_model ?? null}
      />
      <div className="ml-1 h-1.5 flex-1 max-w-[80px] overflow-hidden rounded-full bg-black/10 dark:bg-white/10">
        <div
          className="h-full rounded-full transition-all duration-500"
          style={{ width: `${pct}%`, backgroundColor: tier.bar }}
        />
      </div>
      <span className="text-muted-foreground">{tier.text}</span>
    </div>
  );
}
